import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Eye, EyeOff, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '@/context/LanguageContext';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error(t('auth.fill_all')); return; }
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t('auth.welcome_toast', { name: user.name }));
      const redirect = searchParams.get('redirect');
      navigate(user.role === 'admin' ? '/admin' : redirect ? `/${redirect}` : '/dashboard');
    } catch (err) {
      toast.error(t(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Invalid credentials'));
    }
    setLoading(false);
  };

  return (
    <div className="pt-20 min-h-screen flex items-center" data-testid="login-page">
      <div className="max-w-md mx-auto px-6 py-24 w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-10">
            <Crown className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="text-3xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-2">{t('auth.welcome_back')}</h1>
            <p className="text-sm text-[#A3A19C]">{t('auth.signin_sub')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm text-[#A3A19C] mb-2">{t('common.email')}</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
                placeholder="your@email.com" dir="ltr"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm text-[#A3A19C]">{t('common.password')}</label>
                <Link to="/forgot-password" className="text-xs text-[#D4AF37] hover:text-[#E5C07B] transition-colors duration-300" data-testid="login-forgot-password">{t('auth.forgot_link')}</Link>
              </div>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 pe-12 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
                  placeholder={t('auth.ph_password')}
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[#A3A19C]">
                  {showPass ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full btn-primary" data-testid="login-submit">
              {loading ? t('auth.signing_in') : t('auth.signin')}
            </button>
          </form>

          <p className="text-center text-sm text-[#A3A19C] mt-8">
            {t('auth.no_account')} <Link to={searchParams.get('redirect') ? `/register?redirect=${searchParams.get('redirect')}` : '/register'} className="text-[#D4AF37] hover:text-[#E5C07B] transition-colors duration-300" data-testid="login-to-register">{t('auth.create_one')}</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
